import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Typography,
  Divider,
  LinearProgress,
  Chip,
  Avatar,
  Grid,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  ExpandMore,
  ExpandLess,
  DeleteOutline,
  Add,
} from "@material-ui/icons";
import * as MuiIcons from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

function getPath(obj, path) {
  return path.reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

function setPath(obj, path, value) {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const isArrayIndex = typeof head === "number";
  const base = obj == null
    ? (isArrayIndex ? [] : {})
    : Array.isArray(obj) ? [...obj] : { ...obj };
  base[head] = setPath(base[head], rest, value);
  return base;
}

function evaluateShowIf(expr, values) {
  if (!expr) return true;
  const m = /^\s*(\w+(?:\.\w+)?)\s*(==|!=)\s*(.+)\s*$/.exec(expr);
  if (!m) return true;
  const [, key, op, rawValue] = m;
  const actual = key.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), values);
  let expected;
  const trimmed = rawValue.trim();
  if (/^'.*'$/.test(trimmed) || /^".*"$/.test(trimmed)) {
    expected = trimmed.slice(1, -1);
  } else if (trimmed === "true") expected = true;
  else if (trimmed === "false") expected = false;
  else if (!isNaN(Number(trimmed))) expected = Number(trimmed);
  else expected = trimmed;
  if (op === "==") return actual === expected;
  if (op === "!=") return actual !== expected;
  return true;
}

function MaybeIcon({ name, fontSize = "default", style }) {
  if (!name) return null;
  const Comp = MuiIcons[name];
  if (!Comp) return null;
  return <Comp fontSize={fontSize} style={style} />;
}

// Renderizador básico de markdown — sem dependência externa
function renderMarkdown(src) {
  if (!src) return null;
  const lines = src.split(/\r?\n/);
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^### /.test(line)) {
      out.push(<Typography key={i} variant="subtitle1" gutterBottom>{line.replace(/^### /, "")}</Typography>);
    } else if (/^## /.test(line)) {
      out.push(<Typography key={i} variant="h6" gutterBottom>{line.replace(/^## /, "")}</Typography>);
    } else if (/^# /.test(line)) {
      out.push(<Typography key={i} variant="h5" gutterBottom>{line.replace(/^# /, "")}</Typography>);
    } else if (/^\s*[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s/, ""));
        i++;
      }
      out.push(
        <ul key={i} style={{ marginTop: 0 }}>
          {items.map((it, idx) => <li key={idx}>{renderInline(it)}</li>)}
        </ul>
      );
      continue;
    } else if (line.trim() === "") {
      out.push(<div key={i} style={{ height: 8 }} />);
    } else {
      out.push(<Typography key={i} variant="body2" component="p" style={{ marginBottom: 4 }}>{renderInline(line)}</Typography>);
    }
    i++;
  }
  return <>{out}</>;
}

function renderInline(text) {
  // **bold** e `code` simples
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={m.index}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) parts.push(
      <code key={m.index} style={{ background: "#f0f0f0", padding: "1px 4px", borderRadius: 3 }}>
        {tok.slice(1, -1)}
      </code>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

// ─────────────────────────────────────────────────────────────────
// Source dinâmica
// ─────────────────────────────────────────────────────────────────

const SOURCE_ENDPOINTS = {
  queues: "/queue",
  tags: "/tags/list",
  users: "/users",
};

function adaptSourceResponse(_source, data) {
  if (Array.isArray(data)) {
    return data.map((item) => ({
      value: String(item.id ?? item.value),
      label: item.name ?? item.label ?? String(item.id),
    }));
  }
  const list = data?.records || data?.tags || data?.users || [];
  return list.map((item) => ({
    value: String(item.id ?? item.value),
    label: item.name ?? item.label ?? String(item.id),
  }));
}

function useDynamicOptions(source, staticOptions) {
  const [options, setOptions] = useState(staticOptions || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!source) {
      setOptions(staticOptions || []);
      return;
    }
    const endpoint = SOURCE_ENDPOINTS[source];
    if (!endpoint) return;
    let cancel = false;
    setLoading(true);
    api
      .get(endpoint)
      .then((res) => !cancel && setOptions(adaptSourceResponse(source, res.data)))
      .catch(() => { /* ignore */ })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  return { options, loading };
}

// Hook para ler valor do PluginStorage do plugin via JWT do user.
// Endpoint backend: GET /plugins/:pluginId/storage/:key
function useStorageValue(storageKey, fallback, pluginId) {
  const [value, setValue] = useState(fallback);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!storageKey || !pluginId) {
      setValue(fallback);
      return;
    }
    let cancel = false;
    setLoading(true);
    api
      .get(`/plugins/${pluginId}/storage/${encodeURIComponent(storageKey)}`)
      .then((res) => {
        if (cancel) return;
        const v = res.data?.value;
        setValue(v == null ? fallback : v);
      })
      .catch(() => !cancel && setValue(fallback))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, pluginId]);

  return [value, loading];
}

/**
 * Busca valor dinâmico direto do endpoint do plugin (via routePath proxy).
 * Plugin retorna o que quiser (RAM, BD próprio, cache externo).
 *
 * `refreshInterval` em segundos faz polling.
 */
function useEndpointValue(endpoint, routePath, refreshInterval) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!endpoint || !routePath) {
      setData(null);
      return;
    }
    const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    const url = `${routePath}${path}`;

    let cancel = false;
    const fetchOnce = () => {
      setLoading(true);
      api
        .get(url)
        .then((res) => !cancel && setData(res.data))
        .catch(() => !cancel && setData(null))
        .finally(() => !cancel && setLoading(false));
    };

    fetchOnce();
    let interval;
    if (refreshInterval && refreshInterval > 0) {
      interval = setInterval(fetchOnce, refreshInterval * 1000);
    }
    return () => {
      cancel = true;
      if (interval) clearInterval(interval);
    };
  }, [endpoint, routePath, refreshInterval]);

  return [data, loading];
}

// ─────────────────────────────────────────────────────────────────
// Renderer principal
// ─────────────────────────────────────────────────────────────────

export default function PluginSettingsForm({
  fields = [],
  tabs: declaredTabs = [],
  values,
  onChange,
  pluginId,
  routePath,
}) {
  const tabs = useMemo(() => {
    if (!declaredTabs?.length) return [];
    return [...declaredTabs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [declaredTabs]);

  const [activeTab, setActiveTab] = useState(tabs[0]?.name || "");

  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.name === activeTab)) {
      setActiveTab(tabs[0].name);
    }
  }, [tabs, activeTab]);

  const ctx = { values, onChange, pluginId, routePath };

  if (tabs.length === 0) {
    return <FieldList fields={fields} ctx={ctx} path={[]} />;
  }

  const tabFields = fields.filter((f) => (f.tab || tabs[0].name) === activeTab);

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((t) => (
          <Tab
            key={t.name}
            value={t.name}
            label={t.label}
            icon={t.icon ? <MaybeIcon name={t.icon} fontSize="small" /> : undefined}
          />
        ))}
      </Tabs>
      <Box mt={2}>
        <FieldList fields={tabFields} ctx={ctx} path={[]} />
      </Box>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Lista de fields (recursiva)
// ─────────────────────────────────────────────────────────────────

function FieldList({ fields, ctx, path }) {
  return (
    <>
      {fields.map((field, idx) => {
        if (!evaluateShowIf(field.showIf, ctx.values)) return null;
        return (
          <FieldRenderer
            key={field.name || `__${field.type}_${idx}`}
            field={field}
            ctx={ctx}
            path={path}
          />
        );
      })}
    </>
  );
}

function FieldRenderer({ field, ctx, path }) {
  const { values, onChange } = ctx;

  // Inputs editáveis com valor (têm .name).
  // Display-only (alert, divider, markdown, metric, badge, progress, etc) NÃO mexem no state.
  const hasName = !!field.name;
  const fieldPath = hasName ? [...path, field.name] : path;
  const value = hasName ? getPath(values, fieldPath) : undefined;
  const setValue = useCallback(
    (next) => {
      if (!hasName) return; // segurança: nunca grava em path vazio/sem name
      onChange(setPath(values, fieldPath, next));
    },
    [values, fieldPath, onChange, hasName]
  );

  switch (field.type) {
    // ── Inputs ─────────────────────────────────────────────
    case "switch":
      return <SwitchField field={field} value={value} setValue={setValue} />;
    case "select":
      return <SelectField field={field} value={value ?? field.defaultValue ?? ""} setValue={setValue} />;
    case "textarea":
      return <TextareaField field={field} value={value ?? ""} setValue={setValue} />;
    case "color":
      return <ColorField field={field} value={value ?? field.defaultValue ?? "#000000"} setValue={setValue} />;
    case "date":
    case "time":
      return <DateTimeField field={field} value={value ?? ""} setValue={setValue} />;
    case "number":
    case "text":
    case "password":
      return <TextInputField field={field} value={value ?? ""} setValue={setValue} />;

    // ── Containers ─────────────────────────────────────────
    case "list":
      return (
        <ListFieldRenderer
          field={field}
          value={Array.isArray(value) ? value : []}
          setValue={setValue}
        />
      );
    case "group":
      return <GroupRenderer field={field} ctx={ctx} path={fieldPath} />;
    case "card":
      return <CardContainer field={field} ctx={ctx} path={path} />;
    case "row":
      return <RowContainer field={field} ctx={ctx} path={path} />;
    case "column":
      return <ColumnContainer field={field} ctx={ctx} path={path} />;
    case "tabs":
      return <TabsContainer field={field} ctx={ctx} path={path} />;

    // ── Display ───────────────────────────────────────────
    case "alert":
      return <AlertBlock field={field} />;
    case "divider":
      return <DividerBlock field={field} />;
    case "markdown":
      return <Box my={1}>{renderMarkdown(field.content)}</Box>;
    case "html":
      return (
        <Box
          my={1}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(field.content || "") }}
        />
      );
    case "metric":
      return <MetricBlock field={field} ctx={ctx} />;
    case "badge":
      return <BadgeBlock field={field} ctx={ctx} />;
    case "image":
      return <ImageBlock field={field} />;
    case "progress":
      return <ProgressBlock field={field} ctx={ctx} />;
    case "code":
      return <CodeBlock field={field} />;
    case "spacer":
      return <Box style={{ height: field.height ?? 16 }} />;
    case "dataList":
      return <DataListBlock field={field} ctx={ctx} />;
    case "htmlPage":
      return <HtmlPageBlock field={field} ctx={ctx} />;

    // ── Ação ──────────────────────────────────────────────
    case "button":
      return <ButtonBlock field={field} ctx={ctx} />;

    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────
// Inputs
// ─────────────────────────────────────────────────────────────────

function SwitchField({ field, value, setValue }) {
  return (
    <FormControlLabel
      control={
        <Switch
          checked={!!value}
          onChange={(e) => setValue(e?.target?.checked ?? !value)}
        />
      }
      label={field.label}
      style={{ display: "block", marginBottom: 8 }}
    />
  );
}

function SelectField({ field, value, setValue }) {
  const { options, loading } = useDynamicOptions(field.source, field.options);
  return (
    <TextField
      select
      margin="dense"
      label={field.label}
      fullWidth
      value={value || ""}
      onChange={(e) => {
        const raw = e?.target?.value;
        if (raw == null) return;
        setValue(raw);
      }}
      helperText={field.helpText || (loading ? "Carregando..." : "")}
      required={field.required}
    >
      {(options || []).map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </TextField>
  );
}

function TextareaField({ field, value, setValue }) {
  return (
    <TextField
      margin="dense"
      label={field.label}
      fullWidth
      multiline
      rows={field.rows || 4}
      value={value}
      onChange={(e) => {
        const raw = e?.target?.value;
        if (raw == null) return;
        setValue(raw);
      }}
      placeholder={field.placeholder}
      helperText={field.helpText}
      required={field.required}
    />
  );
}

function ColorField({ field, value, setValue }) {
  return (
    <Box display="flex" alignItems="center" my={1} style={{ gap: 8 }}>
      <input
        type="color"
        value={value}
        onChange={(e) => setValue(e?.target?.value ?? value)}
        style={{ width: 40, height: 40, border: "none", padding: 0, cursor: "pointer" }}
      />
      <TextField
        size="small"
        label={field.label}
        value={value}
        onChange={(e) => setValue(e?.target?.value ?? value)}
        helperText={field.helpText}
      />
    </Box>
  );
}

function DateTimeField({ field, value, setValue }) {
  return (
    <TextField
      type={field.type}
      margin="dense"
      label={field.label}
      fullWidth
      value={value || ""}
      onChange={(e) => setValue(e?.target?.value ?? "")}
      InputLabelProps={{ shrink: true }}
      helperText={field.helpText}
      required={field.required}
    />
  );
}

function TextInputField({ field, value, setValue }) {
  return (
    <TextField
      margin="dense"
      label={field.label}
      type={field.type === "password" ? "password" : field.type === "number" ? "number" : "text"}
      fullWidth
      value={value}
      onChange={(e) => {
        const raw = e?.target?.value;
        if (raw == null) return;
        setValue(field.type === "number" ? Number(raw) : raw);
      }}
      placeholder={field.placeholder}
      helperText={field.helpText}
      required={field.required}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// Containers
// ─────────────────────────────────────────────────────────────────

function GroupRenderer({ field, ctx, path }) {
  return (
    <Card variant="outlined" style={{ marginTop: 8, marginBottom: 8 }}>
      <CardContent>
        {field.label && (
          <Typography variant="subtitle2" gutterBottom>{field.label}</Typography>
        )}
        {field.helpText && (
          <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
            {field.helpText}
          </Typography>
        )}
        <FieldList fields={field.fields || []} ctx={ctx} path={path} />
      </CardContent>
    </Card>
  );
}

function CardContainer({ field, ctx, path }) {
  const variant = field.variant || "default";
  const [collapsed, setCollapsed] = useState(!!field.defaultCollapsed);
  const borderColor = variantColor(variant);

  const header = field.label ? (
    <CardHeader
      avatar={
        field.icon ? (
          <Avatar style={{ background: borderColor, color: "#fff" }}>
            <MaybeIcon name={field.icon} />
          </Avatar>
        ) : null
      }
      title={field.label}
      subheader={field.helpText}
      action={
        field.collapsible ? (
          <IconButton size="small" onClick={() => setCollapsed(c => !c)}>
            {collapsed ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        ) : null
      }
    />
  ) : null;

  return (
    <Card
      variant="outlined"
      style={{
        marginTop: 8,
        marginBottom: 8,
        borderLeft: `4px solid ${borderColor}`
      }}
    >
      {header}
      <Collapse in={!collapsed} unmountOnExit>
        <CardContent style={{ paddingTop: header ? 0 : 16 }}>
          <FieldList fields={field.fields || []} ctx={ctx} path={path} />
        </CardContent>
      </Collapse>
    </Card>
  );
}

function RowContainer({ field, ctx, path }) {
  const gap = field.gap ?? 16;
  return (
    <Grid container spacing={Math.round(gap / 8)} style={{ marginTop: 4, marginBottom: 4 }}>
      {(field.fields || []).map((sub, idx) => {
        if (!evaluateShowIf(sub.showIf, ctx.values)) return null;
        // Se filho não é column, embrulha em column 12
        if (sub.type !== "column") {
          return (
            <Grid item xs={12} key={sub.name || idx}>
              <FieldRenderer field={sub} ctx={ctx} path={path} />
            </Grid>
          );
        }
        const w = Math.max(1, Math.min(12, sub.width ?? 12));
        return (
          <Grid item xs={12} sm={w} key={sub.name || idx}>
            <FieldList fields={sub.fields || []} ctx={ctx} path={path} />
          </Grid>
        );
      })}
    </Grid>
  );
}

function ColumnContainer({ field, ctx, path }) {
  // Quando usado fora de uma row (caso solto), comporta como Box
  return (
    <Box>
      <FieldList fields={field.fields || []} ctx={ctx} path={path} />
    </Box>
  );
}

function TabsContainer({ field, ctx, path }) {
  // Cada child deve ter `name` e `label`. Pode ter `fields`.
  const tabs = (field.fields || []).filter(t => t.name);
  const [active, setActive] = useState(tabs[0]?.name || "");

  if (!tabs.length) return null;
  const current = tabs.find(t => t.name === active) || tabs[0];

  return (
    <Box my={1}>
      <Tabs value={current.name} onChange={(_, v) => setActive(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
        {tabs.map(t => (
          <Tab key={t.name} value={t.name} label={t.label} icon={t.icon ? <MaybeIcon name={t.icon} fontSize="small" /> : undefined} />
        ))}
      </Tabs>
      <Box mt={1}>
        <FieldList fields={current.fields || []} ctx={ctx} path={path} />
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// List (array de objetos)
// ─────────────────────────────────────────────────────────────────

function ListFieldRenderer({ field, value, setValue }) {
  const items = Array.isArray(value) ? value : [];
  const [expandedIdx, setExpandedIdx] = useState(items.length === 1 ? 0 : -1);

  const addItem = () => {
    if (field.maxItems && items.length >= field.maxItems) return;
    const newItem = (field.fields || []).reduce((acc, f) => {
      if (f.defaultValue !== undefined && f.name) acc[f.name] = f.defaultValue;
      return acc;
    }, {});
    const next = [...items, newItem];
    setValue(next);
    setExpandedIdx(next.length - 1);
  };

  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setValue(next);
    if (expandedIdx === idx) setExpandedIdx(-1);
  };

  const updateItem = (idx, newItem) => {
    const next = items.map((it, i) => (i === idx ? newItem : it));
    setValue(next);
  };

  const itemTitle = (item, idx) => {
    if (field.itemLabel && item?.[field.itemLabel]) return String(item[field.itemLabel]);
    return `${field.label || "Item"} #${idx + 1}`;
  };

  return (
    <Box mt={1} mb={1}>
      {field.label && <Typography variant="subtitle2" gutterBottom>{field.label}</Typography>}
      {field.helpText && (
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          {field.helpText}
        </Typography>
      )}

      {items.map((item, idx) => (
        <Card key={idx} variant="outlined" style={{ marginBottom: 6, background: "#fafafa" }}>
          <Box display="flex" alignItems="center" pl={2} pr={1} py={0.5}>
            <Box flex={1}>
              <Typography variant="body2">{itemTitle(item, idx)}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setExpandedIdx(expandedIdx === idx ? -1 : idx)}>
              {expandedIdx === idx ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <IconButton size="small" onClick={() => removeItem(idx)} color="secondary">
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
          <Collapse in={expandedIdx === idx} unmountOnExit>
            <CardContent>
              <FieldList
                fields={field.fields || []}
                ctx={{ values: item, onChange: (newItem) => updateItem(idx, newItem) }}
                path={[]}
              />
            </CardContent>
          </Collapse>
        </Card>
      ))}

      <Button
        size="small"
        startIcon={<Add />}
        onClick={addItem}
        disabled={field.maxItems && items.length >= field.maxItems}
        variant="outlined"
        style={{ marginTop: 4 }}
      >
        {field.addButtonText || `Adicionar ${field.label || "item"}`}
      </Button>
      {field.maxItems && (
        <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
          {items.length}/{field.maxItems}
        </Typography>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// Display blocks
// ─────────────────────────────────────────────────────────────────

function AlertBlock({ field }) {
  const severity = mapVariantToSeverity(field.variant);
  return (
    <Box my={1}>
      <Alert severity={severity} icon={field.icon ? <MaybeIcon name={field.icon} /> : undefined}>
        {field.label && <strong>{field.label}: </strong>}
        {field.content}
      </Alert>
    </Box>
  );
}

function DividerBlock({ field }) {
  return (
    <Box my={2}>
      {field.label ? (
        <Box display="flex" alignItems="center">
          <Box flex={1}><Divider /></Box>
          <Typography variant="caption" color="textSecondary" style={{ padding: "0 12px" }}>
            {field.label}
          </Typography>
          <Box flex={1}><Divider /></Box>
        </Box>
      ) : (
        <Divider />
      )}
    </Box>
  );
}

function MetricBlock({ field, ctx }) {
  const [endpointData] = useEndpointValue(
    field.endpoint,
    ctx?.routePath,
    field.refreshInterval
  );
  const [storageVal] = useStorageValue(
    field.storageKey,
    field.defaultValue,
    ctx?.pluginId
  );
  // Prioridade: endpoint (resposta {value, unit?, helpText?}) → storage → default
  const val = endpointData?.value ?? storageVal;
  const dynamicUnit = endpointData?.unit ?? field.unit;
  const dynamicHelp = endpointData?.helpText ?? field.helpText;
  const color = variantColor(field.variant || "primary");
  return (
    <Box my={1} p={2} style={{ background: "#fff", borderRadius: 6, border: "1px solid #e0e0e0" }}>
      <Box display="flex" alignItems="center" style={{ gap: 12 }}>
        {field.icon && (
          <Avatar style={{ background: color, color: "#fff", width: 40, height: 40 }}>
            <MaybeIcon name={field.icon} />
          </Avatar>
        )}
        <Box>
          <Typography variant="caption" color="textSecondary">{field.label}</Typography>
          <Typography variant="h5" style={{ color, fontWeight: 600 }}>
            {val ?? "—"}
            {dynamicUnit && <Typography component="span" variant="body2" style={{ color: "#666", marginLeft: 4 }}>{dynamicUnit}</Typography>}
          </Typography>
          {dynamicHelp && (
            <Typography variant="caption" color="textSecondary">{dynamicHelp}</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function BadgeBlock({ field, ctx }) {
  const [endpointData] = useEndpointValue(
    field.endpoint,
    ctx?.routePath,
    field.refreshInterval
  );
  const dynamicLabel = endpointData?.value ?? endpointData?.label;
  const color = mapVariantToChipColor(endpointData?.variant || field.variant);
  return (
    <Box my={0.5} display="inline-block" style={{ marginRight: 8 }}>
      <Chip
        size="small"
        label={dynamicLabel || field.label || field.content}
        color={color}
        icon={field.icon ? <MaybeIcon name={field.icon} fontSize="small" /> : undefined}
      />
    </Box>
  );
}

/**
 * Lista dinâmica — busca array de items do endpoint do plugin.
 *
 * Plugin retorna:
 *   { items: [{ title, subtitle?, badge?, icon?, variant? }, ...] }
 *
 * Sem `items` ou erro → renderiza placeholder.
 */
function DataListBlock({ field, ctx }) {
  const [data, loading] = useEndpointValue(
    field.endpoint,
    ctx?.routePath,
    field.refreshInterval
  );
  const items = Array.isArray(data?.items) ? data.items : [];

  return (
    <Box my={1}>
      {field.label && (
        <Box display="flex" alignItems="center" mb={1} style={{ gap: 8 }}>
          {field.icon && <MaybeIcon name={field.icon} fontSize="small" />}
          <Typography variant="subtitle2">{field.label}</Typography>
          {loading && <Typography variant="caption" color="textSecondary">atualizando…</Typography>}
        </Box>
      )}
      {items.length === 0 ? (
        <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
          {loading ? "Carregando..." : (field.placeholder || "Sem dados")}
        </Typography>
      ) : (
        items.map((item, idx) => (
          <Box
            key={idx}
            display="flex"
            alignItems="center"
            p={1}
            mb={0.5}
            style={{ background: "#fafafa", borderRadius: 4, gap: 8 }}
          >
            {item.icon && (
              <Avatar style={{
                background: variantColor(item.variant || "default"),
                color: "#fff",
                width: 32, height: 32
              }}>
                <MaybeIcon name={item.icon} fontSize="small" />
              </Avatar>
            )}
            <Box flex={1}>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {item.title}
              </Typography>
              {item.subtitle && (
                <Typography variant="caption" color="textSecondary">
                  {item.subtitle}
                </Typography>
              )}
            </Box>
            {item.badge && (
              <Chip
                size="small"
                label={item.badge}
                color={mapVariantToChipColor(item.variant)}
              />
            )}
          </Box>
        ))
      )}
    </Box>
  );
}

/**
 * Renderiza HTML servido pelo plugin num iframe.
 * Plugin tem liberdade total — qualquer stack (vanilla JS, htmx, Alpine, React via CDN, etc).
 *
 * URL final: ${routePath}${endpoint}
 * Iframe carrega via src — backend faz proxy autenticado e plugin responde o HTML.
 */
function HtmlPageBlock({ field, ctx }) {
  const path = (field.endpoint || "").startsWith("/") ? field.endpoint : `/${field.endpoint || ""}`;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const src = ctx?.routePath && field.endpoint
    ? `${backendUrl}${ctx.routePath}${path}`
    : null;
  const height = field.height || 600;

  if (!src) {
    return (
      <Box my={1} p={2} style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 4 }}>
        <Typography variant="caption" color="error">
          htmlPage sem endpoint configurado ou plugin sem routePath
        </Typography>
      </Box>
    );
  }

  return (
    <Box my={1} style={{ width: "100%", height, border: "1px solid #e0e0e0", borderRadius: 4, overflow: "hidden" }}>
      <iframe
        title={field.label || "Plugin Page"}
        src={src}
        style={{ width: "100%", height: "100%", border: 0, display: "block" }}
        sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
      />
    </Box>
  );
}

function ImageBlock({ field }) {
  if (!field.src) return null;
  return (
    <Box my={1} display="flex" justifyContent="center">
      <img
        src={field.src}
        alt={field.label || ""}
        style={{
          width: field.imageWidth ?? "auto",
          height: field.imageHeight ?? "auto",
          maxWidth: "100%",
          objectFit: field.imageFit || "contain",
          borderRadius: 4
        }}
      />
    </Box>
  );
}

function ProgressBlock({ field, ctx }) {
  const [endpointData] = useEndpointValue(
    field.endpoint,
    ctx?.routePath,
    field.refreshInterval
  );
  const [storageVal] = useStorageValue(field.storageKey, field.defaultValue || 0, ctx?.pluginId);
  const val = endpointData?.value ?? storageVal;
  const max = field.max ?? 100;
  const pct = Math.min(100, Math.max(0, (Number(val) / max) * 100));
  return (
    <Box my={1}>
      {field.label && (
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="body2">{field.label}</Typography>
          <Typography variant="body2" color="textSecondary">
            {val ?? 0}/{max}
          </Typography>
        </Box>
      )}
      <LinearProgress
        variant="determinate"
        value={pct}
        style={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
}

function CodeBlock({ field }) {
  return (
    <Box
      my={1}
      p={1.5}
      style={{
        background: "#1e1e1e",
        color: "#e0e0e0",
        borderRadius: 4,
        fontFamily: "monospace",
        fontSize: 13,
        overflow: "auto"
      }}
    >
      {field.label && (
        <Box mb={0.5} style={{ color: "#888", fontSize: 11 }}>
          {field.label}{field.language ? ` · ${field.language}` : ""}
        </Box>
      )}
      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{field.content || ""}</pre>
    </Box>
  );
}

function ButtonBlock({ field, ctx }) {
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    if (field.confirm && !window.confirm(field.confirm)) return;
    if (!field.endpoint) {
      toast.warning("Botão sem endpoint configurado");
      return;
    }
    if (!ctx.routePath) {
      toast.warning("Plugin sem rota proxy — recarregue a lista de plugins");
      return;
    }
    setLoading(true);
    try {
      const path = field.endpoint.startsWith("/") ? field.endpoint : `/${field.endpoint}`;
      const url = `${ctx.routePath}${path}`;
      const res = await api.post(url, { values: ctx.values });
      const result = res.data?.result || res.data?.message;
      const severity = res.data?.severity || "success";
      if (result) toast[severity === "error" ? "error" : severity === "warning" ? "warning" : severity === "info" ? "info" : "success"](result);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Erro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box my={1}>
      <Button
        variant={field.variant === "default" ? "outlined" : "contained"}
        color={mapVariantToButtonColor(field.variant)}
        onClick={onClick}
        disabled={loading}
        startIcon={field.icon ? <MaybeIcon name={field.icon} /> : undefined}
      >
        {field.label || "Executar"}
      </Button>
      {field.helpText && (
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
          {field.helpText}
        </Typography>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// Mapping de cores
// ─────────────────────────────────────────────────────────────────

function variantColor(variant) {
  const map = {
    default: "#9e9e9e",
    primary: "#1976d2",
    secondary: "#9c27b0",
    success: "#43a047",
    warning: "#fb8c00",
    error: "#e53935",
    info: "#0288d1",
  };
  return map[variant] || map.default;
}

function mapVariantToSeverity(v) {
  if (v === "error") return "error";
  if (v === "warning") return "warning";
  if (v === "success") return "success";
  return "info";
}

function mapVariantToChipColor(v) {
  if (v === "primary") return "primary";
  if (v === "secondary") return "secondary";
  return "default";
}

function mapVariantToButtonColor(v) {
  if (v === "primary" || v === "secondary") return v;
  if (v === "error") return "secondary";
  return "default";
}

function sanitizeHtml(html) {
  // Sanitização básica — remove <script>, on*=, javascript:
  return String(html)
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "")
    .replace(/javascript:/gi, "");
}
